import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  InputAdornment,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  getTripById,
  getTripPassengers,
  patchTripSale,
  updatePassengerBoarding,
} from "../../api/trips";
import TripSelect from "../../components/TripSelect";
import AddTripPassengerDialog from "./AddTripPassengerDialog";
import { isPlatformAdminUser, isTenantAdminUser, userHasGroup } from "../../utils/auth";

const STATUS_UK_TO_KEY = {
  Оплачено: "paid",
  "Очікує оплату": "pending",
  "Обробка платежу": "processing",
  "Повернення коштів в процесі": "refunding",
  Повернено: "refunded",
  "Не оплачено": "not_paid",
  "Помилка оплати": "failed",
  Скасовано: "cancelled",
  "Не прибув на рейс": "no_show",
};

const fundsHolderLabel = (holder, t) => {
  if (holder === "agent") return t("admin.fundsHolderAgent");
  if (holder === "carrier") return t("admin.fundsHolderCarrier");
  return t("admin.fundsHolderSv");
};

const normalizeStatusKey = (status) =>
  STATUS_UK_TO_KEY[status] || String(status || "").toLowerCase();

export default function TripPassengersPage() {
  const { t } = useTranslation("common");
  const { tripId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDriverOnly =
    userHasGroup("Company Driver") && !userHasGroup("Company Admin");

  const [loading, setLoading] = useState(true);
  const [trip, setTrip] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    boarded: 0,
    no_show: 0,
    pending: 0,
  });
  const [search, setSearch] = useState("");
  const [boardingFilter, setBoardingFilter] = useState("all");
  const [selectedTrip, setSelectedTrip] = useState(
    tripId ? { id: Number(tripId) || tripId } : null
  );
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [soldOut, setSoldOut] = useState(false);
  const [saleSaving, setSaleSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [tripDetails, setTripDetails] = useState(null);
  const canToggleSoldOut = isTenantAdminUser() || isPlatformAdminUser();
  const canAddPassenger = canToggleSoldOut;

  useEffect(() => {
    if (!tripId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const [manifest, tripDetails] = await Promise.all([
          getTripPassengers(tripId),
          getTripById(tripId).catch(() => null),
        ]);
        if (cancelled) return;
        setPassengers(manifest.passengers || []);
        setSummary(
          manifest.summary || {
            total: (manifest.passengers || []).length,
            boarded: 0,
            no_show: 0,
            pending: (manifest.passengers || []).length,
          }
        );
        const details = tripDetails || null;
        const tripSummary = manifest.trip || null;
        setTrip(tripSummary || details);
        setTripDetails(details);
        setSoldOut(Boolean(details?.sold_out));
        if (details) {
          setSelectedTrip(details);
        } else if (tripSummary) {
          setSelectedTrip({
            id: tripSummary.id,
            route: { name: tripSummary.route },
            departure_date: tripSummary.departure_date,
            label: tripSummary.route
              ? `${tripSummary.route} (${dayjs(tripSummary.departure_date).format("DD.MM.YYYY")})`
              : undefined,
          });
        } else {
          setSelectedTrip({ id: Number(tripId) || tripId });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || t("admin.tripPassengersLoadError"));
          setPassengers([]);
          setTrip(null);
          setTripDetails(null);
          setSoldOut(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [tripId, t, reloadKey]);

  const renderStatusChip = (status) => {
    const key = normalizeStatusKey(status);
    const colorMap = {
      paid: "success",
      pending: "info",
      processing: "info",
      refunding: "warning",
      refunded: "warning",
      not_paid: "warning",
      failed: "error",
      cancelled: "error",
      no_show: "warning",
    };
    const known = [
      "paid",
      "pending",
      "processing",
      "not_paid",
      "cancelled",
      "no_show",
      "refunded",
      "refunding",
      "failed",
    ];
    const label = known.includes(key) ? t(`status.${key}`) : status;
    return (
      <Chip
        label={label}
        color={colorMap[key] || "default"}
        size="small"
        variant={["paid", "cancelled", "failed", "no_show"].includes(key) ? "filled" : "outlined"}
      />
    );
  };

  const recomputeSummary = (list) => {
    const next = { total: 0, boarded: 0, no_show: 0, pending: 0 };
    const countable = new Set(["paid", "not_paid", "pending", "processing", "no_show"]);
    for (const p of list) {
      if (!countable.has(normalizeStatusKey(p.payment_status))) continue;
      next.total += 1;
      const b = p.boarding_status || "pending";
      if (b === "boarded") next.boarded += 1;
      else if (b === "no_show") next.no_show += 1;
      else next.pending += 1;
    }
    setSummary(next);
  };

  const handleBoarding = async (passenger, nextStatus) => {
    if (!tripId || !passenger?.boarding_editable) return;
    const current = passenger.boarding_status || "pending";
    const target = current === nextStatus ? "pending" : nextStatus;
    setUpdatingId(passenger.id);
    setError("");
    try {
      const data = await updatePassengerBoarding(tripId, passenger.id, target);
      const keepsOwnStatus = (row) =>
        ["refunded", "refunding", "cancelled"].includes(
          normalizeStatusKey(row.payment_status)
        );
      setPassengers((prev) => {
        const next = prev.map((p) => {
          if (keepsOwnStatus(p)) {
            return p.id === passenger.id
              ? { ...p, boarding_status: data.boarding_status }
              : p;
          }
          if (p.id === passenger.id) {
            return {
              ...p,
              boarding_status: data.boarding_status,
              payment_status: data.booking_status || p.payment_status,
            };
          }
          if (p.booking_number === passenger.booking_number && data.booking_status) {
            return { ...p, payment_status: data.booking_status };
          }
          return p;
        });
        recomputeSummary(next);
        return next;
      });
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          t("admin.boardingUpdateError")
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSoldOut = async (next) => {
    if (!tripId) return;
    const previous = soldOut;
    setSoldOut(next);
    setSaleSaving(true);
    setError("");
    try {
      const data = await patchTripSale(tripId, next);
      setSoldOut(Boolean(data.sold_out));
    } catch {
      setSoldOut(previous);
      setError(t("admin.tripSoldOutError"));
    } finally {
      setSaleSaving(false);
    }
  };

  const filtered = useMemo(() => {
    return passengers.filter((p) => {
      if (boardingFilter !== "all" && (p.boarding_status || "pending") !== boardingFilter) {
        return false;
      }
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return [
        p.name,
        p.surname,
        p.buyer_phone,
        p.buyer_email,
        p.booking_number,
        p.from_city,
        p.to_city,
        p.seat_number != null ? String(p.seat_number) : "",
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [passengers, search, boardingFilter]);

  const departureLabel = trip?.departure_date
    ? dayjs(trip.departure_date).isValid()
      ? dayjs(trip.departure_date).format("DD.MM.YYYY HH:mm")
      : trip.departure_date
    : "—";

  const backTo = isDriverOnly ? "/administrator/expenses" : "/administrator/bookings";

  const boardingControls = (p) => {
    if (!p.boarding_editable) {
      return (
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      );
    }
    const busy = updatingId === p.id;
    const boarded = p.boarding_status === "boarded";
    const noShow = p.boarding_status === "no_show";
    return (
      <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
        <IconButton
          size="small"
          color={boarded ? "success" : "default"}
          disabled={busy}
          title={t("admin.boardingBoarded")}
          onClick={() => handleBoarding(p, "boarded")}
        >
          <CheckCircleOutlineIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          color={noShow ? "error" : "default"}
          disabled={busy}
          title={t("admin.boardingNoShow")}
          onClick={() => handleBoarding(p, "no_show")}
        >
          <HighlightOffIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  };

  return (
    <Container sx={{ mt: 2, mb: 4 }}>
      <MuiLink
        component={RouterLink}
        to={backTo}
        underline="hover"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
      >
        <ArrowBackIcon fontSize="inherit" />
        {isDriverOnly ? t("header.driverExpenses") : t("adminDetails.backToList")}
      </MuiLink>

      <Typography variant="h4" sx={{ mb: 2 }}>
        {t("admin.tripPassengersTitle")}
      </Typography>

      <Box mb={2} maxWidth={640}>
        <TripSelect
          value={selectedTrip}
          onChange={(newTrip) => {
            setSelectedTrip(newTrip);
            if (newTrip?.id) {
              navigate(`/administrator/trips/${newTrip.id}/passengers`);
            }
          }}
          dateFrom={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)}
          fullWidth
        />
      </Box>

      {!tripId ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography color="text.secondary">{t("admin.tripPassengersPickTrip")}</Typography>
        </Paper>
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error && !passengers.length ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <>
          {error ? (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Paper>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              {trip?.route || t("admin.unknownRoute")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {departureLabel}
              {trip?.bus ? ` · ${trip.bus}` : ""}
              {trip?.company ? ` · ${trip.company}` : ""}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Chip
                size="small"
                label={t("admin.boardingSummaryExpected", { count: summary.total })}
              />
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={t("admin.boardingSummaryBoarded", { count: summary.boarded })}
              />
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={t("admin.boardingSummaryNoShow", { count: summary.no_show })}
              />
              <Chip
                size="small"
                variant="outlined"
                label={t("admin.boardingSummaryPending", { count: summary.pending })}
              />
            </Stack>
            {canToggleSoldOut ? (
              <FormControlLabel
                sx={{ mt: 1.5, ml: 0, alignItems: "flex-start" }}
                control={
                  <Switch
                    checked={soldOut}
                    disabled={saleSaving}
                    onChange={(event) => handleSoldOut(event.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{t("admin.tripSoldOut")}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.tripSoldOutHint")}
                    </Typography>
                  </Box>
                }
              />
            ) : soldOut ? (
              <Chip
                size="small"
                color="warning"
                label={t("admin.tripSoldOut")}
                sx={{ mt: 1.5 }}
              />
            ) : null}
            {trip?.boarding_open === false && trip?.departure_date ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {t("admin.boardingOpensAt", {
                  time: dayjs(trip.departure_date)
                    .subtract(1, "hour")
                    .format("DD.MM.YYYY HH:mm"),
                })}
              </Typography>
            ) : null}
            {canAddPassenger ? (
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                sx={{ mt: 1.5 }}
                onClick={() => setAddOpen(true)}
              >
                {t("admin.tripAddPassenger")}
              </Button>
            ) : null}
          </Paper>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={boardingFilter}
            onChange={(_e, v) => v && setBoardingFilter(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="all">{t("admin.boardingFilterAll")}</ToggleButton>
            <ToggleButton value="boarded">{t("admin.boardingBoarded")}</ToggleButton>
            <ToggleButton value="no_show">{t("admin.boardingNoShow")}</ToggleButton>
            <ToggleButton value="pending">{t("admin.boardingPending")}</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("admin.tripPassengersSearch")}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {filtered.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
              <Typography color="text.secondary">{t("admin.tripPassengersEmpty")}</Typography>
            </Paper>
          ) : isMobile ? (
            filtered.map((p) => (
              <Paper
                key={`${p.booking_number}-${p.id}`}
                variant="outlined"
                sx={{ p: 2, mb: 1.5, borderRadius: 2 }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box
                    sx={{ cursor: isDriverOnly ? "default" : "pointer", flex: 1 }}
                    onClick={() => {
                      if (!isDriverOnly) {
                        navigate(`/administrator/bookings/${p.booking_number}`);
                      }
                    }}
                  >
                    <Typography fontWeight={700}>
                      {p.surname} {p.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      #{p.booking_number}
                      {p.is_transfer ? ` · ${t("admin.withTransfer")}` : ""}
                      {p.seat_number != null
                        ? ` · ${t("admin.seatShort", { seat: p.seat_number })}`
                        : ""}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {p.from_city || "—"} — {p.to_city || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.provided_by || t("admin.tripPassengerSourceOwn")}
                      {" · "}
                      {t("admin.fundsHolder")}: {fundsHolderLabel(p.funds_holder, t)}
                    </Typography>
                  </Box>
                  {boardingControls(p)}
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                  <MuiLink
                    href={p.buyer_phone ? `tel:${p.buyer_phone}` : undefined}
                    underline="hover"
                    variant="body2"
                  >
                    {p.buyer_phone || "—"}
                  </MuiLink>
                  {renderStatusChip(p.payment_status_display || p.payment_status)}
                </Stack>
              </Paper>
            ))
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("admin.passengers")}</TableCell>
                    <TableCell>{t("admin.seatColumn")}</TableCell>
                    <TableCell>{t("admin.route")}</TableCell>
                    <TableCell>{t("admin.tripPassengerSource")}</TableCell>
                    <TableCell>{t("admin.fundsHolder")}</TableCell>
                    <TableCell>{t("admin.bookingNumber")}</TableCell>
                    <TableCell>{t("admin.contacts")}</TableCell>
                    <TableCell>{t("admin.status")}</TableCell>
                    <TableCell align="center">{t("admin.boarding")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((p) => (
                    <TableRow key={`${p.booking_number}-${p.id}`} hover>
                      <TableCell
                        sx={{ cursor: isDriverOnly ? "default" : "pointer" }}
                        onClick={() => {
                          if (!isDriverOnly) {
                            navigate(`/administrator/bookings/${p.booking_number}`);
                          }
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {p.surname} {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.type === "child" ? t("adminDetails.child") : t("adminDetails.adult")}
                          {p.is_transfer ? ` · ${t("admin.withTransfer")}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>{p.seat_number ?? "—"}</TableCell>
                      <TableCell>
                        {p.from_city || "—"} — {p.to_city || "—"}
                      </TableCell>
                      <TableCell>
                        {p.provided_by || t("admin.tripPassengerSourceOwn")}
                      </TableCell>
                      <TableCell>{fundsHolderLabel(p.funds_holder, t)}</TableCell>
                      <TableCell>#{p.booking_number}</TableCell>
                      <TableCell>
                        {p.buyer_phone ? (
                          <MuiLink href={`tel:${p.buyer_phone}`} underline="hover">
                            {p.buyer_phone}
                          </MuiLink>
                        ) : (
                          "—"
                        )}
                        {p.buyer_email ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {p.buyer_email}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {renderStatusChip(p.payment_status_display || p.payment_status)}
                      </TableCell>
                      <TableCell align="center">{boardingControls(p)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      <AddTripPassengerDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        trip={tripDetails}
        onCreated={() => setReloadKey((value) => value + 1)}
      />
    </Container>
  );
}
